import React, { useEffect, useRef, useState } from 'react';
import { authAPI } from '../services/api';
import BrandLogo from './BrandLogo';
import CreateClubWizard from './CreateClubWizard';
import { useCoachExperience } from '../context/CoachExperienceContext';
import CoachExperienceEntry from './CoachExperienceEntry';

const cardStyle = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-color)',
  borderRadius: '14px',
  padding: '18px',
};

function makeIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `club-request-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function ClubOnboarding({ user, onComplete, onLogout }) {
  const coaching = useCoachExperience();
  const [mode, setMode] = useState('choose');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [note, setNote] = useState('');
  const [requestState, setRequestState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const requestKey = useRef(makeIdempotencyKey());

  const loadRequests = async () => {
    const res = await authAPI.getClubMembershipRequests();
    const latest = res.data?.items?.[0] || null;
    setRequestState(latest);
    return latest;
  };

  useEffect(() => {
    loadRequests().catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== 'join') return undefined;
    const value = query.trim();
    if (value.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      setError('');
      try {
        const res = await authAPI.searchClubs(value);
        setResults(res.data?.items || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Could not search clubs. Please try again.');
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [mode, query]);

  const submitRequest = async () => {
    if (!selectedClub || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.requestClubMembership(
        selectedClub.id,
        { note: note.trim(), source: 'search' },
        requestKey.current
      );
      setRequestState(res.data?.request || null);
      setSelectedClub(null);
      setMode('choose');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      await authAPI.resendVerification(user?.email);
      setNotice('Verification email sent. Check your inbox and spam folder.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resend the verification email.');
    } finally {
      setLoading(false);
    }
  };

  const cancelRequest = async () => {
    if (!requestState?.id || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.cancelClubMembershipRequest(requestState.id);
      setRequestState(res.data?.request || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not cancel the request.');
    } finally {
      setLoading(false);
    }
  };

  const checkApproval = async () => {
    setLoading(true);
    setError('');
    try {
      const profile = await authAPI.getProfile();
      const currentUser = profile.data?.data || profile.data;
      if (currentUser?.hasClub) {
        localStorage.setItem('user', JSON.stringify(currentUser));
        onComplete(currentUser);
        return;
      }
      await loadRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not refresh your status.');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'create') {
    return (
      <CreateClubWizard
        user={user}
        onComplete={onComplete}
        onLogout={onLogout}
        onBack={() => setMode('choose')}
        initialContext={coaching?.data?.flags?.starter ? coaching.data.draft?.intake : null}
      />
    );
  }

  const pending = requestState?.status === 'pending' || requestState?.status === 'processing';
  const rejected = requestState?.status === 'rejected' || requestState?.status === 'expired';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, sans-serif',
      padding: '16px',
      boxSizing: 'border-box',
    }}>
      <main style={{
        background: 'var(--bg-card)',
        padding: window.innerWidth < 480 ? '24px' : '36px',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '620px',
        border: '1px solid var(--border-color)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <BrandLogo height={42} showBeta />
        </div>
        <h1 style={{ color: 'var(--accent)', fontSize: '24px', textAlign: 'center', marginBottom: 8 }}>
          Connect to your club
        </h1>
        <p style={{ color: 'var(--text-dim)', textAlign: 'center', lineHeight: 1.5, marginBottom: 22 }}>
          Welcome, {user?.name || 'Coach'}. Join your existing club, or create one only if it is brand new to EliteEdge.
        </p>
        {coaching?.data?.flags?.starter && coaching.data.draft?.intake && (
          <p style={{ textAlign: 'center' }}><a href="/coach-start" style={{ color: 'var(--accent)' }}>Back to your private coaching notes</a></p>
        )}
        {!coaching?.data?.draft?.intake && <CoachExperienceEntry />}

        <div aria-live="assertive" aria-atomic="true">
        {error && (
          <div style={{
            background: '#450a0a',
            border: '1px solid #dc2626',
            color: '#fca5a5',
            padding: '10px',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            {error}
            {error.toLowerCase().includes('verify your email') && (
              <button type="button" onClick={resendVerification} disabled={loading} style={{ ...secondaryButtonStyle, marginTop: 10 }}>
                Resend verification email
              </button>
            )}
          </div>
        )}
        </div>
        <div aria-live="polite" aria-atomic="true">
          {notice && <p style={{ color: '#10b981' }}>{notice}</p>}
        </div>

        {pending ? (
          <section style={cardStyle}>
            <h2 style={{ color: 'var(--text-primary)', fontSize: '18px', marginTop: 0 }}>
              Request sent to {requestState.club?.name}
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
              You are signed in, but club features remain locked until a club administrator approves you. We will email you when a decision is made.
            </p>
            {requestState.deliveryStatus === 'failed' && (
              <p style={{ color: '#f59e0b', fontSize: '13px' }}>
                Email delivery was unavailable. The request is still visible in the admin dashboard.
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={checkApproval} disabled={loading} style={primaryButtonStyle}>
                {loading ? 'Refreshing...' : 'Refresh my status'}
              </button>
              {requestState.status === 'pending' && (
                <button type="button" onClick={cancelRequest} disabled={loading} style={secondaryButtonStyle}>
                  Cancel request
                </button>
              )}
            </div>
          </section>
        ) : mode === 'join' ? (
          <section>
            <button type="button" onClick={() => { setMode('choose'); setSelectedClub(null); setError(''); }} style={linkButtonStyle}>
              <span aria-hidden="true">←</span> Back
            </button>
            <label htmlFor="club-search" style={labelStyle}>Search by club name or city</label>
            <input
              id="club-search"
              type="search"
              value={query}
              autoFocus
              maxLength={80}
              onChange={event => { setQuery(event.target.value); setSelectedClub(null); }}
              placeholder="e.g. Riverside or Melbourne"
              style={inputStyle}
              aria-describedby="club-search-help"
            />
            <p id="club-search-help" style={{ color: 'var(--text-dim)', fontSize: 12 }}>
              Enter at least two characters. Select a matching club from the results.
            </p>
            <div id="club-search-results" aria-live="polite">
              {searching && <p style={{ color: 'var(--text-dim)' }}>Searching...</p>}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <p role="status" style={{ color: 'var(--text-dim)' }}>
                  No clubs matched. Try another club name or city.
                </p>
              )}
              {results.map(club => (
                <button
                  key={club.id}
                  type="button"
                  aria-pressed={selectedClub?.id === club.id}
                  onClick={() => { setSelectedClub(club); requestKey.current = makeIdempotencyKey(); }}
                  style={{
                    ...resultButtonStyle,
                    borderColor: selectedClub?.id === club.id ? 'var(--accent)' : 'var(--border-color)',
                  }}
                >
                  <strong>{club.name}</strong>
                  <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>
                    {[club.sport, club.location].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}
            </div>

            {selectedClub && (
              <div style={{ ...cardStyle, marginTop: 16 }}>
                <h2 style={{ color: 'var(--text-primary)', fontSize: '17px', marginTop: 0 }}>
                  Request to join {selectedClub.name}
                </h2>
                <label htmlFor="join-note" style={labelStyle}>Note for the club admin (optional)</label>
                <textarea
                  id="join-note"
                  value={note}
                  maxLength={280}
                  rows={3}
                  onChange={event => setNote(event.target.value)}
                  placeholder="For example: I am the new U12 assistant coach."
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
                <button type="button" onClick={submitRequest} disabled={loading} style={primaryButtonStyle}>
                  {loading ? 'Sending...' : 'Send request'}
                </button>
              </div>
            )}
          </section>
        ) : (
          <>
            {rejected && (
              <div role="status" style={{ ...cardStyle, marginBottom: 14, borderColor: '#f59e0b' }}>
                <strong style={{ color: '#f59e0b' }}>
                  {requestState.status === 'expired'
                    ? `Your request for ${requestState.club?.name} expired before it was reviewed.`
                    : `Your request for ${requestState.club?.name} was not approved.`}
                </strong>
                {requestState.decisionReason && (
                  <p style={{ color: 'var(--text-secondary)', marginBottom: 0 }}>{requestState.decisionReason}</p>
                )}
              </div>
            )}
            <div style={{ display: 'grid', gap: 14 }}>
              <button type="button" onClick={() => setMode('join')} style={choiceButtonStyle}>
                <span style={{ fontSize: 26 }} aria-hidden="true">🔍</span>
                <span>
                  <strong style={{ display: 'block', fontSize: '17px' }}>Join an existing club</strong>
                  <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>Most coaches should start here.</span>
                </span>
              </button>
              <button type="button" onClick={() => setMode('create')} style={choiceButtonStyle}>
                <span style={{ fontSize: 26 }} aria-hidden="true">➕</span>
                <span>
                  <strong style={{ display: 'block', fontSize: '17px' }}>Create a brand-new club</strong>
                  <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>Only when your club is not already listed.</span>
                </span>
              </button>
            </div>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 22 }}>
          <p style={{ color: 'var(--text-dim)', fontSize: '12px' }}>
            Signed in as <strong style={{ color: 'var(--text-primary)' }}>{user?.email}</strong>
          </p>
          <button type="button" onClick={onLogout} style={secondaryButtonStyle}>Log out</button>
        </div>
      </main>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  color: 'var(--text-secondary)',
  fontSize: '13px',
  fontWeight: 600,
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid var(--border-input)',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '16px',
  boxSizing: 'border-box',
};

const primaryButtonStyle = {
  padding: '11px 18px',
  background: 'var(--accent)',
  color: 'var(--text-on-accent)',
  border: 'none',
  borderRadius: '8px',
  fontWeight: 700,
  cursor: 'pointer',
};

const secondaryButtonStyle = {
  padding: '10px 16px',
  background: 'transparent',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-input)',
  borderRadius: '8px',
  fontWeight: 600,
  cursor: 'pointer',
};

const linkButtonStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--accent)',
  padding: '0 0 14px',
  cursor: 'pointer',
  fontWeight: 600,
};

const choiceButtonStyle = {
  ...cardStyle,
  color: 'var(--text-primary)',
  textAlign: 'left',
  display: 'flex',
  gap: 14,
  alignItems: 'center',
  cursor: 'pointer',
  width: '100%',
};

const resultButtonStyle = {
  width: '100%',
  padding: '13px',
  marginTop: '9px',
  borderRadius: '10px',
  border: '1px solid var(--border-color)',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  cursor: 'pointer',
};
